import * as themes from 'devextreme/ui/themes';
import { refreshTheme } from 'devextreme/viz/themes';
// import SpeedDialAction from 'devextreme/ui/speed_dial_action';
// import { debugLog } from './enci.log.js';

export class ThemeManager {
  constructor() {
    this.DARK_SWATCH = 'dx-swatch-dark';
    this.THEMES = {
      DARK: {
        NAME: 'fluent.blue.dark.custom.compact',
        SWATCH: this.DARK_SWATCH,
      },
      LIGHT: {
        NAME: 'fluent.blue.light.custom.compact',
        SWATCH: '',
      },
    };

    // Get the force light mode setting from window.appsettings
    this.forceLightMode = window.appsettings?.forceLightMode ?? true;

    // Initialize state - always light if forced
    this.currentTheme = this.THEMES.LIGHT.NAME;
    this.initialized = false;

    // Bind methods
    this.toggleTheme = this.toggleTheme.bind(this);
    this.handleSystemThemeChange = this.handleSystemThemeChange.bind(this);
    this.showContent = this.showContent.bind(this);
  }

  /**
   * Initialize the theme manager
   */
  async init() {
    if (this.initialized) return;

    try {
      document.addEventListener('DOMContentLoaded', () => {
        // If forcing light mode, ensure we're always in light mode
        if (this.forceLightMode) {
          this.setLightMode();
        } else {
          // Only check system preferences if we're not forcing light mode
          const darkMode = this.handleThemeStorage();
          const preferredTheme = window.matchMedia?.('(prefers-color-scheme: dark)').matches;

          if (darkMode || (!darkMode && preferredTheme)) {
            this.currentTheme = this.THEMES.DARK.NAME;
            document.body.classList.add(this.DARK_SWATCH);
            document.documentElement.setAttribute('data-bs-theme', 'dark');
          }
        }

        themes.current(this.currentTheme);
        this.showContent();
      });

      // Only add the system theme listener if we're not forcing light mode
      if (!this.forceLightMode) {
        this.addMediaEventListener();
      }

      // Set up click events for theme switcher
      this.addOnClickEvent();

      this.initialized = true;
      console.log('Theme manager initialized');
    } catch (error) {
      console.error('Failed to initialize theme manager:', error);
    }
  }

  /**
   * Add media query event listener for system theme changes
   */
  addMediaEventListener() {
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', this.handleSystemThemeChange);
  }

  /**
   * Add click event listener for theme switch button
   */
  addOnClickEvent() {
    document.querySelectorAll('.theme-switch-button').forEach(button => {
      button.addEventListener('click', this.toggleTheme);
    });
  }

  /**
   * Handle system theme change
   */
  handleSystemThemeChange(event) {
    // If forcing light mode, ignore system theme changes
    if (this.forceLightMode) return;

    event.matches ? this.setDarkMode() : this.setLightMode();
  }

  /**
   * Apply dark mode theme
   */
  setDarkMode() {
    themes.current(this.THEMES.DARK.NAME);
    document.body.classList.add(this.DARK_SWATCH);
    // document.body.setAttribute('data-theme', 'dark');
    document.documentElement.setAttribute('data-bs-theme', 'dark');
  }

  /**
   * Apply light mode theme
   */
  setLightMode() {
    themes.current(this.THEMES.LIGHT.NAME);
    document.body.classList.remove(this.DARK_SWATCH);
    // document.body.setAttribute('data-theme', 'default');
    document.documentElement.setAttribute('data-bs-theme', 'light');
  }

  /**
   * Handle theme storage - get or set theme preference
   * @param {boolean} [isDark=null] - If provided, sets the theme. If null, gets the current theme
   * @returns {boolean|void} Returns current theme preference if getting, nothing if setting
   */
  handleThemeStorage(isDark = null) {
    const THEME_KEY = 'theme';

    // Get current theme
    if (isDark === null) {
      return JSON.parse(localStorage.getItem(THEME_KEY));
    }

    // Set new theme
    localStorage.setItem(THEME_KEY, JSON.stringify(isDark));
  }

  /**
   * Check if current theme is dark
   */
  isDarkMode() {
    // If forcing light mode, always return false
    if (this.forceLightMode) return false;

    return document.body.classList.contains(this.DARK_SWATCH);
  }

  /**
   * Toggle between light and dark themes
   */
  toggleTheme() {
    // If forcing light mode, prevent toggle and ensure light mode
    if (this.forceLightMode) {
      this.setLightMode();
      return false;
    }

    this.handleThemeStorage(!this.isDarkMode());
    const newTheme = this.isDarkMode() ? 'light' : 'dark';
    newTheme === 'dark' ? this.setDarkMode() : this.setLightMode();

    // Refresh DevExtreme components
    refreshTheme();
    // SpeedDialAction.getInstance()?.repaint();

    // Return the current state
    return this.isDarkMode();
  }

  /**
   * Show content after theme is applied
   */
  showContent() {
    document.body.style.visibility = 'visible';
    document.body.style.opacity = 1;
  }

  /**
   * Remove event listeners and clean up
   */
  cleanup() {
    if (!this.forceLightMode) {
      window
        .matchMedia('(prefers-color-scheme: dark)')
        .removeEventListener('change', this.handleSystemThemeChange);
    }

    document.querySelectorAll('.theme-switch-button').forEach(button => {
      button.removeEventListener('click', this.toggleTheme);
    });
  }
}

// Export a singleton instance
export const themeManager = new ThemeManager();
